import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Sun, Battery, Wind, Home as HomeIcon, Droplet, Square } from 'lucide-react';
import { companyInfo, solutions, partnersIntro, strengthsQuote } from '../data/mockData';

const SolutionsPage = () => {
  const icons = { Sun, Battery, Wind, Home: HomeIcon, Droplet, Square };

  // Ordre des solutions : Panneaux, Batteries, Pompes, Boilers, Isolation, Fenêtres (pas Micro-onduleurs)
  const orderedSolutions = [
    solutions.find(s => s.id === 'solar'),
    solutions.find(s => s.id === 'batteries'),
    solutions.find(s => s.id === 'heat-pumps'),
    solutions.find(s => s.id === 'thermodynamic-boilers'),
    solutions.find(s => s.id === 'insulation'),
    solutions.find(s => s.id === 'windows')
  ].filter(Boolean);

  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section - Compact */}
      <section className="bg-gradient-to-br from-[#03224c] to-[#03224c]/90 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">
              Nos solutions énergétiques
            </h1>
            <p className="text-lg text-gray-200 leading-relaxed mb-6">
              Des écosystèmes énergétiques complets pour maximiser votre autonomie et réduire durablement vos coûts en Suisse romande.
            </p>
            <Link to="/contact">
              <button className="px-8 py-3 bg-[#e57200] text-white rounded-lg font-bold hover:bg-[#e57200]/90 transition-all shadow-lg">
                DEMANDER UN DEVIS
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Solutions Detail - Compact */}
      <section className="py-10 bg-white">
        <div className="container mx-auto px-4">
          <div className="space-y-12">
            {orderedSolutions.map((solution, index) => {
              const Icon = icons[solution.icon];
              const isEven = index % 2 === 0;

              return (
                <div
                  key={solution.id}
                  className={`grid lg:grid-cols-2 gap-8 items-center bg-gradient-to-br ${
                    isEven ? 'from-gray-50 to-white' : 'from-blue-50 to-white'
                  } p-8 rounded-2xl shadow-md`}
                >
                  {/* Image */}
                  <div className={`${!isEven ? 'lg:order-2' : ''}`}>
                    <div className="rounded-xl overflow-hidden shadow-lg">
                      <img
                        src={solution.image}
                        alt={solution.title}
                        className="w-full h-72 object-cover hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                  </div>

                  {/* Content */}
                  <div className={`${!isEven ? 'lg:order-1' : ''}`}>
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="w-12 h-12 rounded-lg bg-[#e57200] flex items-center justify-center">
                        <Icon className="text-white" size={24} />
                      </div>
                      <h2 className="text-2xl md:text-3xl font-bold text-[#03224c]">
                        {solution.title}
                      </h2>
                    </div>
                    
                    <p className="text-gray-700 mb-4 leading-relaxed">
                      {solution.description}
                    </p>

                    {/* Warranty */}
                    {solution.warranty && (
                      <div className="mb-4 p-3 bg-[#e57200]/10 rounded-lg border-l-4 border-[#e57200]">
                        <div className="flex items-center space-x-2">
                          <CheckCircle2 className="text-[#e57200]" size={18} />
                          <span className="text-sm font-bold text-gray-800">{solution.warranty}</span>
                        </div>
                      </div>
                    )}

                    {/* Benefits - Compact */}
                    <div className="space-y-2">
                      <div className="text-xs font-bold text-gray-700 mb-2 uppercase tracking-wide">
                        Avantages
                      </div>
                      <div className="grid sm:grid-cols-2 gap-2">
                        {solution.benefits.map((benefit, idx) => (
                          <div key={idx} className="flex items-start space-x-1.5">
                            <CheckCircle2
                              className="text-[#e57200] flex-shrink-0 mt-0.5"
                              size={14}
                            />
                            <span className="text-xs text-gray-700">{benefit}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* CTA Button */}
                    <div className="mt-6">
                      <Link to="/contact">
                        <button className="w-full sm:w-auto px-6 py-2.5 bg-[#e57200] text-white rounded-lg font-semibold hover:bg-[#e57200]/90 transition-all shadow-md">
                          Demander un devis
                        </button>
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Partners Section - Brand Logos from Homepage */}
      <section className="py-8 bg-gray-50 border-y border-gray-200">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-[#03224c] text-center mb-6">
            Nos marques partenaires
          </h2>
          <div className="flex flex-wrap justify-center items-center gap-12">
            <div className="h-16 flex items-center">
              <img src="/images/brands/sunpower.png" alt="Sunpower" className="h-12 w-auto object-contain grayscale hover:grayscale-0 transition-all" />
            </div>
            <div className="h-16 flex items-center">
              <img src="/images/brands/enphase.png" alt="Enphase" className="h-12 w-auto object-contain grayscale hover:grayscale-0 transition-all" />
            </div>
            <div className="h-16 flex items-center">
              <img src="/images/brands/atmoce.png" alt="Atmoce" className="h-12 w-auto object-contain grayscale hover:grayscale-0 transition-all" />
            </div>
            <div className="h-16 flex items-center">
              <img src="/images/brands/vaillant.png" alt="Vaillant" className="h-12 w-auto object-contain grayscale hover:grayscale-0 transition-all" />
            </div>
            <div className="h-16 flex items-center">
              <img src="/images/brands/panasonic.png" alt="Panasonic" className="h-12 w-auto object-contain grayscale hover:grayscale-0 transition-all" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Updated */}
      <section className="py-12 bg-gradient-to-r from-[#03224c] to-[#03224c]/90">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h2 className="text-3xl font-bold mb-4">
              Prêt à démarrer votre projet ?
            </h2>
            <p className="text-lg mb-6 text-gray-200">
              Contactez-nous pour une étude personnalisée et gratuite. Notre équipe vous accompagne de A à Z pour votre projet de rénovation énergétique.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/contact">
                <button className="px-8 py-3 bg-[#e57200] text-white rounded-lg font-bold hover:bg-[#e57200]/90 transition-all shadow-lg">
                  DEVIS GRATUIT
                </button>
              </Link>
              <a href="tel:021391789">
                <button className="px-8 py-3 border-2 border-white text-white rounded-lg font-bold hover:bg-white/10 transition-all">
                  021 391 78 99
                </button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SolutionsPage;
